// convex/lib/config/core.ts
// Core configuration - no imports from helpers.ts to avoid circular dependencies

import type { DomainGroup, Edition } from "./types";
import { ALL_EDITIONS, LOCAL_INSTALL_EDITIONS } from "./types";
import { systemDomain } from "./domains/system";
import { securityDomain } from "./domains/security";
import { clientsDomain } from "./domains/clients";
import { reportsDomain } from "./domains/reports";
import { subscriptionsDomain } from "./domains/subscriptions";
import { notificationsDomain } from "./domains/notifications";
import { uiDomain } from "./domains/ui";
import { documentsDomain } from "./domains/documents";
import { collectionsDomain } from "./domains/collections";
import { integrationsDomain } from "./domains/integrations";
import { mcpDomain } from "./domains/mcp";
import { aiDomain } from "./domains/ai";
import { salesDomain } from "./domains/sales";
import { invoicesDomain } from "./domains/invoices";
import { creditNotesDomain } from "./domains/credit_notes";
import { debitNotesDomain } from "./domains/debit_notes";
import { catalogDomain } from "./domains/catalog";
import { quotationsDomain } from "./domains/quotations";
import { stockDomain } from "./domains/stock";
import { operationsDomain } from "./domains/operations";
import { configurationDomain } from "./domains/configuration";

// ────────────────────────────────────────────────────────────────────────
// Edición de distribución
// ────────────────────────────────────────────────────────────────────────
// La edición decide qué dominios se ensamblan. Las joyas (Tier 2) declaran
// `editions` sin "onprem", así que en el build on-prem quedan fuera del
// catálogo de módulos. Default "saas" → en la nube se incluye TODO (sin cambio
// de comportamiento). Ver docs/editions-architecture.md
//
// NOTA: este filtro es la capa de METADATA (navegación, permisos, planes,
// tableConfigs). La exclusión FÍSICA del código de las joyas (para que ni
// siquiera entre al artefacto on-prem) la hace `scripts/prepare-onprem.ts`,
// que ahora corre dentro de Dockerfile.frontend y Dockerfile.deployer: borra
// la lógica de operations/reports y la emisión de licencias antes de compilar.
// Las imágenes publicadas ya NO contienen esa IP. Ver docs/editions-architecture.md

function resolveEdition(): Edition {
  let raw: string | undefined;
  // Backend (Convex / Node): EDITION inyectada en build/runtime
  try {
    if (typeof process !== "undefined" && process.env?.EDITION) {
      raw = process.env.EDITION;
    }
  } catch {
    /* entorno sin process */
  }
  // Frontend (Vite): VITE_EDITION inyectada en build via define
  if (!raw) {
    try {
      raw = import.meta.env?.VITE_EDITION as string | undefined;
    } catch {
      /* entorno sin import.meta */
    }
  }
  return (ALL_EDITIONS as readonly string[]).includes(raw ?? "")
    ? (raw as Edition)
    : "saas";
}

export const EDITION: Edition = resolveEdition();

/**
 * True si el build es una instalación local self-host (onprem o free_desktop).
 * Usar en vez de `EDITION === "onprem"` para lógica compartida: single-tenant,
 * onboarding desde token, bootstrap del primer admin, etc.
 */
export function isLocalInstallEdition(edition: Edition = EDITION): boolean {
  return (LOCAL_INSTALL_EDITIONS as readonly string[]).includes(edition);
}

const domainInEdition = (d: DomainGroup): boolean =>
  !d.editions || d.editions.includes(EDITION);

/** Catálogo completo, sin filtrar por edición (para super admin / herramientas). */
export const allDomainsUnfiltered: DomainGroup[] = [
  aiDomain,
  systemDomain,
  securityDomain,
  clientsDomain,
  reportsDomain,
  subscriptionsDomain,
  notificationsDomain,
  uiDomain,
  documentsDomain,

  salesDomain,
  invoicesDomain,
  creditNotesDomain,
  debitNotesDomain,
  catalogDomain,
  quotationsDomain,
  stockDomain,
  operationsDomain,
  configurationDomain,
  collectionsDomain,
  integrationsDomain,
  mcpDomain,
];

/** Dominios activos en la edición actual. Es la fuente de verdad por defecto. */
export const allDomains: DomainGroup[] =
  allDomainsUnfiltered.filter(domainInEdition);

export const ERP_MODULES_CONFIG = {
  modules: Object.fromEntries(allDomains.map((d) => [d.id, d])),
  metadata: {
    version: "3.0.0",
    lastUpdated: "2026-04-10",
    description: "Configuracion unificada por dominio",
    edition: EDITION,
  },
};

export const tableConfigs = Object.fromEntries(
  allDomains
    .flatMap((d) => d.modules)
    .flatMap((m) => m.tables)
    .map((t) => [t.tableName, t]),
);
